/*
 * cart-app-refresh.js
 * ------------------------------------------------------------
 * Keeps THIS theme's cart drawer in sync after ANY add-to-cart, including
 * adds made by the cover/bundle product-selectors app.
 *
 * Why this exists:
 *   The product-selectors app intercepts add-to-cart and, on success, tries
 *   to re-render the cart using Dawn's section/element names (e.g.
 *   "cart-icon-bubble") which do not exist in this theme. The cart DATA
 *   updates (POST 200) but the theme's real drawer never refreshes — so it
 *   opens still showing its page-load contents and the new addition is
 *   missing until a manual refresh.
 *
 * How it renders the drawer (important):
 *   It requests the cart sections through a POST to /cart/update.js with the
 *   `sections` param. POST cart endpoints are NEVER edge-cached and always
 *   render with the live cart — unlike a GET to "?sections=", which Shopify
 *   can serve from cache rendered with an EMPTY cart (the bug we hit: the
 *   refresh ran but injected a 0-item drawer even though /cart.js had 2).
 *   Sending no `updates` makes it a no-op read that just returns the cart +
 *   freshly rendered sections.
 *
 * Triggers (any of):
 *   - window.fetch hook on /cart/add
 *   - XMLHttpRequest hook on /cart/add (jQuery / legacy app adds)
 *   - MutationObserver: whenever the drawer OPENS (covers any add path)
 * ------------------------------------------------------------
 */
(function () {
  var DEBUG = true; // set false to silence the [cart-refresh] console logs
  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[cart-refresh]'].concat([].slice.call(arguments)));
    }
  }

  // Mirror the theme's own ProductForm.getSectionsToRender() (product.min.js).
  var SECTIONS = [
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ];

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  var rendering = false;
  var lastRefresh = 0;

  function isCartAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);
  }
  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return root.replace(/\/$/, '') + path;
  }

  function sectionInnerHTML(html, selector) {
    try {
      var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) { return null; }
  }
  function renderedLineItems() {
    var d = document.getElementById('Cart-Drawer');
    return d ? d.querySelectorAll('.product-cart-item').length : 0;
  }
  function shownCount() {
    var t = document.getElementById('CartDrawerTitle');
    var m = t && t.textContent && t.textContent.match(/\((\d+)\)/);
    return m ? parseInt(m[1], 10) : null;
  }
  function openDrawer() {
    var d = document.getElementById('Cart-Drawer');
    if (d) { document.body.classList.add('open-cc'); d.classList.add('active'); }
  }

  // Re-render the theme's real cart sections via a POST (never cached, always
  // reflects the live cart) and inject them into the drawer + count bubble.
  function refreshCart(thenOpen) {
    if (rendering || !origFetch) return;
    rendering = true;
    origFetch(cartRoute('/cart/update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        sections: 'cart-drawer,cart-bubble',
        sections_url: window.location.pathname
      }),
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.sections) { log('no sections in update response'); return; }
        SECTIONS.forEach(function (s) {
          var host = document.getElementById(s.id);
          if (!host || !cart.sections[s.section]) return;
          // Match native logic: target the inner node, else the host itself
          // (the drawer's .cart-drawer class sits ON #Cart-Drawer).
          var target = host.querySelector(s.selector) || host;
          var inner = sectionInnerHTML(cart.sections[s.section], s.selector);
          if (inner != null) target.innerHTML = inner;
        });
        // Re-init the quantity steppers exactly like the theme's renderContents
        // (product.min.js) — otherwise the +/- buttons render unstyled ("1+").
        if (typeof QuantityInput !== 'undefined') {
          var drawer = document.getElementById('Cart-Drawer');
          if (drawer) {
            drawer.querySelectorAll('.quantity:not(.buttons_added)').forEach(function (q) {
              try { new QuantityInput(q); } catch (e) {}
            });
          }
        }
        if (typeof CartDrawer !== 'undefined') { try { new CartDrawer(); } catch (e) {} }
        if (thenOpen) openDrawer();
        lastRefresh = Date.now();
        log('refreshed; item_count =', cart.item_count, 'line items now =', renderedLineItems(), 'title =', shownCount());
      })
      .catch(function (e) { log('refresh error', e); })
      .finally(function () { rendering = false; });
  }

  // An add just happened → refresh + open. Second pass covers a slow app.
  function onAdd(via) {
    log('add detected via', via);
    setTimeout(function () { refreshCart(true); }, 120);
    setTimeout(function () { refreshCart(true); }, 700);
  }

  // The drawer opened → make sure it is fresh (skip if we just refreshed).
  function onOpen() {
    if (Date.now() - lastRefresh < 1200) return;
    log('drawer opened; refreshing');
    refreshCart(false);
  }

  // --- Hook window.fetch ---
  if (window.fetch) {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(null, arguments);
      if (isCartAddUrl(url)) {
        p.then(function (res) { if (res && res.ok) onAdd('fetch'); }).catch(function () {});
      }
      return p;
    };
  }

  // --- Hook XMLHttpRequest (jQuery / legacy app adds) ---
  if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__isCartAdd = isCartAddUrl(url);
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__isCartAdd) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) onAdd('xhr');
        });
      }
      return _send.apply(this, arguments);
    };
  }

  // --- Observe the drawer opening (catches any add path + manual opens) ---
  function watchDrawer() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer || !window.MutationObserver) return;
    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var active = drawer.classList.contains('active');
      if (active && !wasActive) onOpen();
      wasActive = active;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    log('watching cart drawer');
  }
  if (document.readyState !== 'loading') watchDrawer();
  else document.addEventListener('DOMContentLoaded', watchDrawer);
})();
